import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useSchoolBrand } from '../../hooks/useSchoolBrand';
import {
  HomeIcon, CalendarDaysIcon, AcademicCapIcon, BanknotesIcon,
  BookOpenIcon, MegaphoneIcon, UserCircleIcon, DocumentChartBarIcon,
  ClipboardDocumentListIcon, StarIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { useAllModules } from '../../hooks/useAccess';

const navigation = [
  { name: 'Dashboard',          href: '/student/dashboard',  icon: HomeIcon,                  module: 'dashboard' },
  { name: 'Attendance',         href: '/student/attendance', icon: CalendarDaysIcon,           module: 'attendance' },
  { name: 'Results',            href: '/student/results',    icon: AcademicCapIcon,            module: 'results' },
  { name: 'Grade Report',       href: '/student/grade-report', icon: DocumentChartBarIcon,     module: 'results' },
  { name: 'Fees',               href: '/student/fees',       icon: BanknotesIcon,              module: 'fees' },
  { name: 'Materials',          href: '/student/materials',  icon: BookOpenIcon,               module: 'materials' },
  { name: 'Online Assessments', href: '/student/quizzes',    icon: ClipboardDocumentListIcon,  badge: 'quizzes', module: 'quizzes' },
  { name: 'Notices',            href: '/student/notices',    icon: MegaphoneIcon, badge: 'notices', module: 'notices' },
  { name: 'Evaluation',         href: '/student/feedback',   icon: StarIcon,                   module: 'profile' },
  { name: 'Timetable',          href: '/student/timetable',  icon: CalendarDaysIcon,           module: 'timetable' },
  { name: 'Datesheets',         href: '/student/datesheets', icon: DocumentChartBarIcon,       module: 'datesheets' },
  { name: 'Profile',            href: '/student/profile',    icon: UserCircleIcon,             module: 'profile' },
];

const sections = [
  { label: 'Main',     items: navigation.slice(0, 5) },
  { label: 'Learning', items: navigation.slice(5, 9) },
  { label: 'Account',  items: navigation.slice(9) },
];

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&display=swap');
  .sidebar { width:240px; min-width:240px; min-height:100vh; background:#0f1123; display:flex; flex-direction:column; overflow-x:hidden; font-family:'Sora',sans-serif; border-right:1px solid rgba(255,255,255,.04); }
  .sidebar-close { display:none; }
  @media (max-width:768px) {
    .sidebar-close { display:flex; align-items:center; justify-content:center; position:absolute; top:16px; right:16px; width:28px; height:28px; border-radius:8px; background:rgba(255,255,255,.08); border:none; cursor:pointer; }
    .sidebar-close svg { width:15px; height:15px; stroke:white; stroke-width:2; }
    .sidebar-brand { position:relative; }
  }
  .sidebar-brand { padding:24px 20px 20px; border-bottom:1px solid rgba(255,255,255,.06); flex-shrink:0; }
  .sidebar-brand-row { display:flex; align-items:center; gap:10px; }
  .sidebar-brand-icon { width:34px; height:34px; background:#8b5cf6; border-radius:10px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .sidebar-brand-icon svg { width:18px; height:18px; fill:none; stroke:white; stroke-width:2; }
  .sidebar-brand-name { font-size:.95rem; font-weight:700; color:#fff; letter-spacing:-.02em; line-height:1; }
  .sidebar-brand-sub  { font-size:.68rem; color:#4a5075; margin-top:3px; letter-spacing:.04em; text-transform:uppercase; }
  .sidebar-nav { flex:1; padding:12px; overflow-y:auto; overflow-x:hidden; scrollbar-width:none; }
  .sidebar-nav::-webkit-scrollbar { display:none; }
  .sidebar-section-label { font-size:.62rem; font-weight:600; color:#353b5c; text-transform:uppercase; letter-spacing:.08em; padding:16px 8px 6px; }
  .sidebar-link { display:flex; align-items:center; gap:10px; padding:9px 10px; border-radius:10px; text-decoration:none; color:#7880a4; font-size:.82rem; font-weight:500; transition:background .15s,color .15s; margin-bottom:2px; position:relative; }
  .sidebar-link-text { flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .sidebar-link:hover { background:rgba(255,255,255,.04); color:#c8ccde; }
  .sidebar-link.active { background:rgba(139,92,246,.12); color:#a78bfa; }
  .sidebar-link.active::before { content:''; position:absolute; left:-12px; top:25%; bottom:25%; width:3px; background:#8b5cf6; border-radius:0 3px 3px 0; }
  .sidebar-link svg { width:17px; height:17px; stroke-width:1.7; flex-shrink:0; }
  .sidebar-footer { padding:16px 12px; border-top:1px solid rgba(255,255,255,.05); flex-shrink:0; }
  .sidebar-footer-text { font-size:.68rem; color:#353b5c; text-align:center; letter-spacing:.02em; }
  .notice-badge { margin-left:auto; min-width:18px; height:18px; padding:0 5px; border-radius:99px; background:#ef4444; color:white; font-size:.6rem; font-weight:700; display:flex; align-items:center; justify-content:center; line-height:1; flex-shrink:0; }
  .feedback-badge { margin-left:auto; min-width:18px; height:18px; padding:0 5px; border-radius:99px; background:#8b5cf6; color:white; font-size:.6rem; font-weight:700; display:flex; align-items:center; justify-content:center; line-height:1; flex-shrink:0; }
`;

const StudentSidebar = ({ onClose }) => {
  const location = useLocation();
  const { user }  = useAuth();
  const { brand } = useSchoolBrand();
  const { modules: allowedModules } = useAllModules();
  const [noticeCount,     setNoticeCount]     = useState(0);
  const [quizCount,       setQuizCount]       = useState(0);
  const [pendingFeedback, setPendingFeedback] = useState(0);

  useEffect(() => {
    api.get('/student/notices/unread-count')
      .then(r => setNoticeCount(r.data.count || 0))
      .catch(() => {});
    api.get('/student/quizzes/unread-count')
      .then(r => setQuizCount(r.data.count || 0))
      .catch(() => {});
    api.get('/student/feedback')
      .then(r => setPendingFeedback((r.data||[]).filter(f => !f.isSubmitted).length))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (location.pathname.startsWith('/student/notices')) {
      api.post('/student/notices/mark-read').catch(() => {});
      setNoticeCount(0);
    }
    if (location.pathname.startsWith('/student/quizzes')) {
      api.post('/student/quizzes/mark-read').catch(() => {});
      setQuizCount(0);
    }
    if (location.pathname.startsWith('/student/feedback')) setPendingFeedback(0);
  }, [location.pathname]);

  const badgeCount = (key) => (key === 'notices' ? noticeCount : key === 'quizzes' ? quizCount : 0);

  return (
    <>
      <style>{style}</style>
      <div className="sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-row">
            {brand.logo
              ? <img src={brand.logo} alt="logo" style={{width:34,height:34,borderRadius:10,objectFit:'contain',background:'rgba(255,255,255,.95)',padding:2,border:'1px solid rgba(255,255,255,.12)',flexShrink:0}}/>
              : <div className="sidebar-brand-icon">
                  <svg viewBox="0 0 24 24"><path d="M12 3L2 9l10 6 10-6-10-6zM2 15l10 6 10-6M2 12l10 6 10-6" /></svg>
                </div>
            }
            <div>
              <div className="sidebar-brand-name">{brand.name || 'School Portal'}</div>
              <div className="sidebar-brand-sub">Student Panel</div>
            </div>
          </div>
          {onClose && <button className="sidebar-close" onClick={onClose}><XMarkIcon /></button>}
        </div>
        <nav className="sidebar-nav">
          {sections.map(sec => (
            <div key={sec.label}>
              <div className="sidebar-section-label">{sec.label}</div>
              {sec.items.filter(item => !allowedModules || allowedModules.includes(item.module)).map(item => {
                const isActive = location.pathname.startsWith(item.href);
                return (
                  <Link key={item.name} to={item.href}
                    className={`sidebar-link${isActive ? ' active' : ''}`}
                    onClick={onClose}>
                    <item.icon />
                    <span className="sidebar-link-text">{item.name}</span>
                    {item.badge && badgeCount(item.badge) > 0 && (
                      <span className="notice-badge">{badgeCount(item.badge) > 99 ? '99+' : badgeCount(item.badge)}</span>
                    )}
                    {item.name === 'Evaluation' && pendingFeedback > 0 && (
                      <span className="feedback-badge">{pendingFeedback}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div className="sidebar-footer-text">v1.0.0 · Student Portal</div>
        </div>
      </div>
    </>
  );
};

export default StudentSidebar;